/**
 * Save a rendered element as an A4 PDF.
 *
 * Viewers keep their content in scrolling panels, and html2pdf captures only
 * what is visible, so the element is cloned off-screen with scrolling and
 * height limits removed first; the full document is captured, not the window.
 */
export async function downloadElementPdf(element, filename, options = {}) {
  if (!element) throw new Error('Nothing to export');
  const { default: html2pdf } = await import('html2pdf.js');

  const holder = document.createElement('div');
  holder.style.cssText =
    'position:fixed;left:-10000px;top:0;z-index:-1;background:#fff;pointer-events:none;';
  const width = Math.max(element.scrollWidth, element.offsetWidth, 760);
  holder.style.width = `${width}px`;

  const clone = element.cloneNode(true);
  const unclip = (node) => {
    node.style.maxHeight = 'none';
    node.style.height = 'auto';
    node.style.overflow = 'visible';
  };
  unclip(clone);
  // html2pdf lays the clone out in a page-wide (~730px) box; wider layouts
  // would spill past it and be cut off. Keep the real width; the capture is
  // then scaled down to fit A4.
  clone.style.width = `${width}px`;
  clone.style.maxWidth = 'none';
  // Buttons and other UI chrome marked to skip stay out of the document.
  clone.querySelectorAll('[data-pdf-ignore]').forEach((node) => node.remove());
  holder.appendChild(clone);
  document.body.appendChild(holder);
  // Computed styles exist only once attached.
  clone.querySelectorAll('*').forEach((node) => {
    const style = window.getComputedStyle(node);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') unclip(node);
  });

  try {
    const worker = html2pdf()
      .set({
        margin: 8,
        filename: String(filename || 'document.pdf').replace(/[\\/:*?"<>|]+/g, '-'),
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          scrollY: 0,
          backgroundColor: '#ffffff',
          width,
          windowWidth: width,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(clone);
    // { output: 'blob' } returns the PDF (e.g. to attach to an email) instead of saving it.
    if (options.output === 'blob') return await worker.outputPdf('blob');
    await worker.save();
    return null;
  } finally {
    holder.remove();
  }
}

/**
 * Download a stored file (S3 link). Cross-origin links ignore the `download`
 * attribute and open in the tab instead, so fetch the bytes and save them;
 * if the storage blocks that, fall back to opening it in a new tab.
 */
export async function downloadStoredFile(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename || 'file';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1500);
    return true;
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    return false;
  }
}
